import { community_chat } from "../../js/path";

export default {
  data() {
    return {
      // Names members see on official messages — an admin's own message vs. a
      // News Review post (see path.js).
      officialSenderName: community_chat.officialSenderName,
      newsSenderName: community_chat.newsSenderName,
      // Broadcast (one message -> many groups)
      postableGroups: [],
      broadcastForm: { body: "", type: "text", media: null },
      broadcastSelected: [],
      broadcastSending: false,
      broadcastUploading: false,
      broadcastResult: null,
      // Groups list
      groups: [],
      groupFilter: "",
      groupFields: [
        { key: "name", label: "Group" },
        { key: "messageCount", label: "Messages" },
        { key: "lastPreview", label: "Last Message" },
        { key: "lastAt", label: "Last Activity" },
        { key: "action", label: "" },
      ],
      // Thread
      groupId: null,
      groupName: "",
      messages: [],
      loadingMessages: false,
      form: { body: "" },
      replyingTo: null,
      uploading: false,
      pollTimer: null,
      // Admin-created communities + their join-request queue
      communities: [],
      communityFields: [
        { key: "name", label: "Community" },
        { key: "joinPolicy", label: "Joining" },
        { key: "visibility", label: "Visibility" },
        { key: "memberCount", label: "Members" },
        { key: "pendingCount", label: "Requests" },
        { key: "isActive", label: "Active" },
        { key: "action", label: "" },
      ],
      communityForm: {
        name: "",
        description: "",
        avatar: "",
        visibility: "public",
        joinPolicy: "approval",
      },
      savingCommunity: false,
      uploadingLogo: false,
      // News review queue
      newsItems: [],
      newsStatus: "pending",
      newsPending: 0,
      newsPage: 1,
      newsLastPage: 1,
      newsGroups: [],
      fetchingNews: false,
      postingNews: null,
      requests: [],
      requestsTotal: 0,
      requestStatus: "pending",
      activeCommunity: null,
      deciding: null,
      // --- filtering + pagination ------------------------------------------
      // Every one of these lists arrives whole — the speciality x state
      // catalogue alone is ~1,700 rows — so filtering and paging are
      // client-side and only the current page is ever rendered.
      perPage: 25,
      perPageOptions: [10, 25, 50, 100],
      communityFilter: "",
      communityTypeFilter: "all", // all | custom | derived
      communityPage: 1,
      groupPage: 1,
      newsGroupFilter: "",
      newsGroupPage: 1,
    };
  },
  computed: {
    // Groups list is fetched in full, so the group-name filter is client-side.
    filteredGroups() {
      const q = (this.groupFilter || "").trim().toLowerCase();
      if (!q) return this.groups;
      return this.groups.filter((g) =>
        (g.name || "").toLowerCase().includes(q)
      );
    },
    pagedGroups() {
      return this.paginate(this.filteredGroups, this.groupPage);
    },

    /**
     * Communities matching the search box and the type filter. Search covers
     * the state too, so "Kerala" finds every speciality group there.
     */
    filteredCommunities() {
      const q = (this.communityFilter || "").trim().toLowerCase();
      const type = this.communityTypeFilter;
      return this.communities.filter((c) => {
        if (type === "custom" && c.type !== "custom") return false;
        if (type === "derived" && c.type === "custom") return false;
        if (!q) return true;
        return (
          (c.name || "").toLowerCase().includes(q) ||
          (c.state || "").toLowerCase().includes(q) ||
          (c.description || "").toLowerCase().includes(q)
        );
      });
    },
    pagedCommunities() {
      return this.paginate(this.filteredCommunities, this.communityPage);
    },

    filteredNewsGroups() {
      const q = (this.newsGroupFilter || "").trim().toLowerCase();
      if (!q) return this.newsGroups;
      return this.newsGroups.filter((g) =>
        (g.name || "").toLowerCase().includes(q)
      );
    },
    pagedNewsGroups() {
      return this.paginate(this.filteredNewsGroups, this.newsGroupPage);
    },
  },
  watch: {
    // A filter change can leave the current page beyond the end of the list,
    // which renders as an empty table — always jump back to page 1.
    communityFilter() {
      this.communityPage = 1;
    },
    communityTypeFilter() {
      this.communityPage = 1;
    },
    groupFilter() {
      this.groupPage = 1;
    },
    newsGroupFilter() {
      this.newsGroupPage = 1;
    },
    perPage() {
      this.communityPage = 1;
      this.groupPage = 1;
      this.newsGroupPage = 1;
    },
  },
  methods: {
    messagesUrl(id) {
      return `${community_chat.groupsUrl}/${id}/messages`;
    },

    /** One page of a client-side list. */
    paginate(list, page) {
      const start = (page - 1) * this.perPage;
      return list.slice(start, start + this.perPage);
    },

    /** "Showing 26–50 of 1,734" — reads as empty when a filter matches nothing. */
    showingRange(list, page) {
      if (!list.length) return "Showing 0";
      const from = (page - 1) * this.perPage + 1;
      const to = Math.min(page * this.perPage, list.length);
      return `Showing ${from}–${to} of ${list.length}`;
    },

    // ---- Groups list ----
    async fetchGroups() {
      this.$store.commit("loader/updateStatus", true);
      try {
        const data = await this.getRequest(community_chat.groupsUrl);
        if (data.status) {
          this.groups = data.response || [];
        }
      } catch (err) {
        this.$store.commit("toast/updateStatus", {
          status: true,
          icon: "error",
          title: err && err.data ? err.data.message : "Please try again!",
        });
      }
      this.$store.commit("loader/updateStatus", false);
    },

    // ---- Thread ----
    async fetchMessages(id, opts = {}) {
      try {
        const data = await this.getRequest(this.messagesUrl(id));
        if (data.status) {
          this.messages = data.response || [];
          // Backfill the group name from message data if not passed via route.
          if (!this.groupName && this.messages.length) {
            const named = this.messages.find((m) => m.communityName);
            if (named) this.groupName = named.communityName;
          }
        }
      } catch (err) {
        if (!opts.silent) {
          this.$store.commit("toast/updateStatus", {
            status: true,
            icon: "error",
            title: err && err.data ? err.data.message : "Could not load messages.",
          });
        }
      }
    },

    async sendMessage() {
      const body = (this.form.body || "").trim();
      if (!body) return;

      // A message that is just a URL is sent as a link (server builds a preview).
      const type = /^https?:\/\//i.test(body) ? "link" : "text";
      const payload = { body, type };
      if (this.replyingTo) payload.replyToId = this.replyingTo._id;

      this.$store.commit("loader/updateStatus", true);
      // NOTE: request.js postRequest resolves only on success (it swallows
      // errors with its own toast + loader reset), so code below runs on success.
      const data = await this.postRequest(this.messagesUrl(this.groupId), payload);
      if (data && data.status) {
        this.form.body = "";
        this.replyingTo = null;
        this.$store.commit("toast/updateStatus", {
          status: true,
          icon: "success",
          title: data.message || "Message sent.",
        });
        this.$store.commit("loader/updateStatus", false);
        await this.fetchMessages(this.groupId, { silent: true });
      }
    },

    // Upload an image/video, then send it as a message (optionally as a reply).
    async sendMedia(file) {
      if (!file) return;
      this.uploading = true;
      this.$store.commit("loader/updateStatus", true);

      const fd = new FormData();
      fd.append("file", file);
      const up = await this.postRequest(community_chat.uploadUrl, fd);
      if (up && up.status && up.response && up.response.media) {
        const payload = {
          type: up.response.type,
          media: up.response.media,
        };
        if (this.replyingTo) payload.replyToId = this.replyingTo._id;
        const data = await this.postRequest(this.messagesUrl(this.groupId), payload);
        if (data && data.status) {
          this.replyingTo = null;
          await this.fetchMessages(this.groupId, { silent: true });
        }
      }
      this.uploading = false;
      this.$store.commit("loader/updateStatus", false);
    },

    onPickFile(e) {
      const file = e.target.files && e.target.files[0];
      if (file) this.sendMedia(file);
      e.target.value = "";
    },

    startReply(m) {
      this.replyingTo = m;
    },
    cancelReply() {
      this.replyingTo = null;
    },
    // Short label + snippet for a quoted reply preview.
    replySnippet(m) {
      if (!m) return "";
      if (m.type === "image") return "📷 Photo";
      if (m.type === "video") return "🎥 Video";
      if (m.type === "link") return "🔗 " + ((m.linkPreview && m.linkPreview.title) || m.body || "Link");
      return (m.body || "").slice(0, 120);
    },

    startPolling(id) {
      this.stopPolling();
      // Refresh the thread periodically so member replies appear (REST + poll).
      this.pollTimer = setInterval(() => {
        this.fetchMessages(id, { silent: true });
      }, 5000);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    // ---- Communities (admin-created) ----
    membersUrl(code) {
      return `${community_chat.communitiesUrl}/${code}/members`;
    },

    async fetchCommunities() {
      this.$store.commit("loader/updateStatus", true);
      try {
        const data = await this.getRequest(community_chat.communitiesUrl);
        if (data.status) {
          this.communities = data.response || [];
        }
      } catch (err) {
        this.$store.commit("toast/updateStatus", {
          status: true,
          icon: "error",
          title: err && err.data ? err.data.message : "Could not load communities.",
        });
      }
      this.$store.commit("loader/updateStatus", false);
    },

    /**
     * Upload a logo image to S3 via community-service and return its URL.
     * Reuses the same media endpoint the broadcast composer uses.
     */
    async uploadLogo(file) {
      if (!file) return "";
      this.uploadingLogo = true;
      const fd = new FormData();
      fd.append("file", file);
      const up = await this.postRequest(community_chat.uploadUrl, fd);
      this.uploadingLogo = false;
      if (up && up.status && up.response && up.response.media) {
        return up.response.media.url;
      }
      return "";
    },

    // Create form: pick a logo before saving.
    async onPickLogo(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!file) return;
      const url = await this.uploadLogo(file);
      if (url) this.communityForm.avatar = url;
    },

    // List row: replace the logo of an existing community straight away.
    async onChangeLogo(community, e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!file) return;
      const url = await this.uploadLogo(file);
      if (!url) return;
      const data = await this.putRequest(
        `${community_chat.communitiesUrl}/${community.code}`,
        { avatar: url }
      );
      if (data && data.status) {
        this.$store.commit("toast/updateStatus", {
          status: true,
          icon: "success",
          title: "Logo updated.",
        });
        await this.fetchCommunities();
      }
    },

    async createCommunity() {
      const name = (this.communityForm.name || "").trim();
      if (!name) return;
      this.savingCommunity = true;
      const data = await this.postRequest(community_chat.communitiesUrl, {
        ...this.communityForm,
        name,
      });
      if (data && data.status) {
        this.$store.commit("toast/updateStatus", {
          status: true,
          icon: "success",
          title: data.message || "Community created.",
        });
        this.communityForm = {
          name: "",
          description: "",
          avatar: "",
          visibility: "public",
          joinPolicy: "approval",
        };
        await this.fetchCommunities();
      }
      this.savingCommunity = false;
    },

    // Toggle a community active/inactive; inactive ones leave members' lists
    // and drop out of the discovery directory.
    async toggleActive(c) {
      const data = await this.putRequest(
        `${community_chat.communitiesUrl}/${c.code}`,
        { isActive: !c.isActive }
      );
      if (data && data.status) {
        await this.fetchCommunities();
      }
    },

    async fetchRequests(code, status = "pending") {
      this.requestStatus = status;
      try {
        const data = await this.getRequest(
          `${this.membersUrl(code)}?status=${status}`
        );
        if (data.status) {
          this.requests = (data.response && data.response.data) || [];
          this.requestsTotal = (data.response && data.response.total) || 0;
        }
      } catch (err) {
        this.$store.commit("toast/updateStatus", {
          status: true,
          icon: "error",
          title: err && err.data ? err.data.message : "Could not load requests.",
        });
      }
    },

    async decideRequest(code, memberId, approve) {
      this.deciding = memberId;
      const data = await this.postRequest(
        `${this.membersUrl(code)}/${memberId}/decision`,
        { approve }
      );
      if (data && data.status) {
        this.$store.commit("toast/updateStatus", {
          status: true,
          icon: "success",
          title: data.message,
        });
        await this.fetchRequests(code, this.requestStatus);
      }
      this.deciding = null;
    },

    // ---- Broadcast (one message -> many groups) ----
    async fetchPostableGroups() {
      try {
        const data = await this.getRequest(community_chat.postableGroupsUrl);
        if (data.status) this.postableGroups = data.response || [];
      } catch (err) {
        this.postableGroups = [];
      }
    },

    // Upload once; the same media object is sent to every selected group.
    async onPickBroadcastFile(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!file) return;

      this.broadcastUploading = true;
      const fd = new FormData();
      fd.append("file", file);
      const up = await this.postRequest(community_chat.uploadUrl, fd);
      this.broadcastUploading = false;

      if (up && up.status && up.response && up.response.media) {
        this.broadcastForm.media = up.response.media;
        this.broadcastForm.type = up.response.type; // image | video
      }
    },

    clearBroadcastMedia() {
      this.broadcastForm.media = null;
      this.broadcastForm.type = "text";
    },

    async sendBroadcast() {
      const body = (this.broadcastForm.body || "").trim();
      const media = this.broadcastForm.media;
      if ((!body && !media) || !this.broadcastSelected.length) return;

      // A message that is only a URL is sent as a link so the service builds
      // a preview card; media keeps its own image/video type.
      let type = this.broadcastForm.type;
      if (!media) type = /^https?:\/\/\S+$/i.test(body) ? "link" : "text";

      this.broadcastSending = true;
      const payload = {
        communityIds: this.broadcastSelected,
        type,
        body,
      };
      if (media) payload.media = media;

      const data = await this.postRequest(community_chat.broadcastUrl, payload);
      this.broadcastSending = false;

      if (data && data.status) {
        this.broadcastResult = data.response || { posted: [], failed: [] };
        this.$store.commit("toast/updateStatus", {
          status: true,
          icon: "success",
          title: `Sent to ${(this.broadcastResult.posted || []).length} group(s).`,
        });
        this.broadcastForm = { body: "", type: "text", media: null };
        this.broadcastSelected = [];
      }
    },

    // ---- News review ----
    async fetchNews(page = 1) {
      this.$store.commit("loader/updateStatus", true);
      try {
        const data = await this.getRequest(
          `${community_chat.newsUrl}?status=${this.newsStatus}&page=${page}`
        );
        if (data.status) {
          const r = data.response || {};
          this.newsItems = r.data || [];
          this.newsPending = r.pending || 0;
          this.newsPage = r.current_page || 1;
          this.newsLastPage = r.last_page || 1;
        }
      } catch (err) {
        this.$store.commit("toast/updateStatus", {
          status: true,
          icon: "error",
          title: err && err.data ? err.data.message : "Could not load news.",
        });
      }
      this.$store.commit("loader/updateStatus", false);
    },

    async fetchNewsGroups() {
      try {
        const data = await this.getRequest(`${community_chat.newsUrl}/groups`);
        if (data.status) this.newsGroups = data.response || [];
      } catch (err) {
        this.newsGroups = [];
      }
    },

    // Pull fresh candidates from the news sources into the queue.
    async refreshNewsQueue() {
      this.fetchingNews = true;
      const data = await this.postRequest(`${community_chat.newsUrl}/fetch`, {});
      if (data && data.status) {
        this.$store.commit("toast/updateStatus", {
          status: true,
          icon: "success",
          title: `${(data.response && data.response.queued) || 0} new article(s) queued.`,
        });
        await this.fetchNews(1);
      }
      this.fetchingNews = false;
    },

    async approveNews(id, communityIds, note) {
      this.postingNews = id;
      const data = await this.postRequest(`${community_chat.newsUrl}/${id}/approve`, {
        communityIds,
        note: note || "",
      });
      this.postingNews = null;
      if (data && data.status) {
        const posted = (data.response && data.response.posted) || [];
        const failed = (data.response && data.response.failed) || [];
        this.$store.commit("toast/updateStatus", {
          status: true,
          icon: "success",
          title: `Posted to ${posted.length} group(s)${
            failed.length ? `, ${failed.length} skipped` : ""
          }.`,
        });
        await this.fetchNews(this.newsPage);
        return true;
      }
      return false;
    },

    async rejectNews(id) {
      const data = await this.postRequest(`${community_chat.newsUrl}/${id}/reject`, {});
      if (data && data.status) {
        await this.fetchNews(this.newsPage);
      }
    },

    // ---- View helpers ----
    isAdminMsg(m) {
      return m.senderRole === "admin";
    },
    senderLabel(m) {
      if (m.senderName) return m.senderName;
      if (m.senderId === "news") return community_chat.newsSenderName;
      return m.senderRole === "admin"
        ? community_chat.officialSenderName
        : "Member " + m.senderId;
    },
    formatTime(ts) {
      if (!ts) return "";
      const d = new Date(ts);
      return d.toLocaleString();
    },
  },
};
